import { Outlet, NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard, CalendarDays, CalendarRange, Users, Clock, Settings, LogOut, Globe,
} from "lucide-react";

const ITEMS = [
  { to: "/doctor", label: "Overview", icon: LayoutDashboard, end: true, tid: "nav-overview" },
  { to: "/doctor/appointments", label: "Appointments", icon: CalendarDays, tid: "nav-appointments" },
  { to: "/doctor/calendar", label: "Calendar", icon: CalendarRange, tid: "nav-calendar" },
  { to: "/doctor/patients", label: "Patients", icon: Users, tid: "nav-patients" },
  { to: "/doctor/availability", label: "Availability", icon: Clock, tid: "nav-availability" },
  { to: "/doctor/settings", label: "Settings", icon: Settings, tid: "nav-settings" },
];

export default function DoctorLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="min-h-screen flex bg-[#F8FAFC]" data-testid="doctor-dashboard">
      <aside className="hidden md:flex md:w-64 flex-col bg-[#0F172A] text-slate-100">
        <div className="h-16 px-5 flex items-center gap-2 border-b border-slate-800">
          <div className="w-8 h-8 rounded-lg bg-emerald-600 grid place-items-center text-xs font-bold">DP</div>
          <div className="leading-tight">
            <div className="font-display font-bold text-sm">Dr Profile</div>
            <div className="text-[11px] text-slate-400">Clinic Dashboard</div>
          </div>
        </div>
        <nav className="flex-1 py-5 px-3 space-y-1">
          {ITEMS.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-slate-800 text-white border-l-2 border-emerald-500" : "text-slate-400 hover:text-white hover:bg-slate-800/60"
                }`
              }
              data-testid={it.tid}
            >
              <it.icon size={16} />
              {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800 space-y-1">
          <Link to="/" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-slate-800/60" data-testid="nav-view-public-site">
            <Globe size={16} /> View public site
          </Link>
          <button
            onClick={async () => { await logout(); nav("/doctor/login"); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-slate-800/60"
            data-testid="btn-logout"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 px-4 md:px-8 flex items-center justify-between">
          <div className="md:hidden flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#0F172A] text-white grid place-items-center text-xs font-bold">DP</div>
            <span className="font-display font-bold">Dashboard</span>
          </div>
          <div className="hidden md:block">
            <div className="font-display font-bold text-slate-900">Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</div>
            <div className="text-xs text-slate-500">Manage appointments and clinic settings</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right text-sm">
              <div className="font-medium text-slate-800">{user?.email}</div>
              <div className="text-xs text-slate-500 capitalize">{user?.role}</div>
            </div>
            <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-800 grid place-items-center font-semibold">
              {(user?.name || user?.email || "D").slice(0, 1).toUpperCase()}
            </div>
          </div>
        </header>
        {/* Mobile nav */}
        <nav className="md:hidden bg-white border-b border-slate-200 overflow-x-auto flex gap-1 px-2 py-2">
          {ITEMS.map((it) => (
            <NavLink
              key={it.to} to={it.to} end={it.end}
              className={({ isActive }) =>
                `shrink-0 px-3 py-2 rounded-lg text-xs font-medium ${isActive ? "bg-slate-900 text-white" : "text-slate-600 bg-slate-100"}`
              }
            >
              {it.label}
            </NavLink>
          ))}
        </nav>
        <main className="flex-1 p-4 md:p-8 overflow-x-hidden fade-in">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
